import { BASE_MEDIA_URL } from '../../api/client';
import { Broadcast, ClipboardText, IdentificationBadge, Lightning, Paperclip, Robot, User } from '@phosphor-icons/react';

const SOURCE_STYLE = {
  customer:   { color: '#475569', bg: '#f8fafc', border: '#e2e8f0', dot: '#64748b', Icon: ClipboardText },
  dispatcher: { color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', Icon: Broadcast },
  ai:         { color: '#6d28d9', bg: '#faf5ff', border: '#e9d5ff', dot: '#8b5cf6', Icon: Robot },
  manager:    { color: '#1B3068', bg: '#EDF3FC', border: '#B7C9EC', dot: '#29AAE1', Icon: IdentificationBadge },
  auto:       { color: '#0f766e', bg: '#f0fdfa', border: '#99f6e4', dot: '#14b8a6', Icon: Lightning },
  employee:   { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', dot: '#22c55e', Icon: User },
};

function styleFor(source) {
  return SOURCE_STYLE[source] || SOURCE_STYLE.employee;
}

function stepHeadline(step) {
  if (step.kind === 'created') {
    return step.by_label ? `ثبت توسط ${step.by_label}` : 'ثبت تیکت';
  }
  if (step.to_label && step.by_label) {
    return `${step.by_label} ← ${step.to_label}`;
  }
  if (step.to_label) return `ارجاع به ${step.to_label}`;
  if (step.by_label) return `توسط ${step.by_label}`;
  return step.source_label || 'ارجاع';
}

function currentBannerText(step, viewerUsername) {
  if (!step) return '';
  const toYou = viewerUsername && step.to_username && step.to_username === viewerUsername;
  const byLabel = step.by_label || step.source_label;
  if (toYou) {
    return `این تیکت توسط ${byLabel} (${step.source_label}) به شما ارجاع شده است.`;
  }
  if (step.to_label) {
    return `الان نزد ${step.to_label} — ارجاع‌شده توسط ${byLabel} (${step.source_label})`;
  }
  return `وضعیت فعلی: ${step.source_label}`;
}

/** Vertical execution path: who referred the ticket, and how (dispatcher / AI / manager / auto). */
export default function TicketReferralRoadmap({ steps, viewerUsername, compact = false }) {
  const list = Array.isArray(steps) ? steps : [];
  if (!list.length) return null;

  const current = [...list].reverse().find((s) => s.current) || list[list.length - 1];
  const banner = currentBannerText(current, viewerUsername);

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: compact ? 12 : 16,
        border: '1px solid rgba(35,58,122,0.12)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: compact ? '10px 14px' : '12px 20px',
          background: 'linear-gradient(135deg,rgba(15,23,42,0.04),rgba(35,58,122,0.05))',
          borderBottom: '1px solid #e2e8f0',
          fontSize: compact ? 12 : 13,
          fontWeight: 700,
          color: '#0f172a',
        }}
      >
        🗺️ روند اجرا و ارجاع
      </div>
      {banner && (
        <div
          style={{
            padding: compact ? '8px 14px' : '10px 20px',
            background: '#EDF3FC',
            borderBottom: '1px solid #D9E4F7',
            fontSize: compact ? 12 : 13,
            color: '#1e40af',
            fontWeight: 600,
            lineHeight: 1.6,
          }}
        >
          {banner}
        </div>
      )}
      <div style={{ padding: compact ? '10px 14px 12px' : '14px 20px 16px', display: 'flex', flexDirection: 'column' }}>
        {list.map((step, i) => {
          const st = styleFor(step.source);
          const last = i === list.length - 1;
          const currentStep = !!step.current;
          return (
            <div key={`${step.source}-${step.at}-${i}`} style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
                <span
                  style={{
                    width: currentStep ? 14 : 10,
                    height: currentStep ? 14 : 10,
                    borderRadius: '50%',
                    background: st.dot,
                    boxShadow: currentStep ? `0 0 0 4px ${st.bg}` : 'none',
                    marginTop: 4,
                    flexShrink: 0,
                  }}
                />
                {!last && (
                  <span style={{ flex: 1, width: 2, background: '#e2e8f0', margin: '4px 0 0' }} />
                )}
              </div>
              <div
                style={{
                  flex: 1,
                  marginBottom: last ? 0 : 12,
                  padding: compact ? '8px 10px' : '10px 12px',
                  borderRadius: 10,
                  background: currentStep ? st.bg : '#fafbfc',
                  border: `1px solid ${currentStep ? st.border : '#eef2f7'}`,
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      color: st.color,
                      background: st.bg,
                      border: `1px solid ${st.border}`,
                      borderRadius: 20,
                      padding: '2px 8px',
                    }}
                  >
                    <st.Icon size={12} weight="bold" style={{ verticalAlign: '-2px' }} /> {step.source_label}
                  </span>
                  {currentStep && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#1B3068', background: '#D9E4F7', borderRadius: 20, padding: '2px 8px' }}>
                      وضعیت فعلی
                    </span>
                  )}
                  {step.at && (
                    <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 'auto' }}>{step.at}</span>
                  )}
                </div>
                <div style={{ fontSize: compact ? 12 : 13, color: '#0f172a', fontWeight: 600, lineHeight: 1.5 }}>
                  {stepHeadline(step)}
                </div>
                {step.kind !== 'created' && step.by_label && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    ارجاع‌دهنده: {step.by_label}
                    {step.to_label ? ` · گیرنده: ${step.to_label}` : ''}
                    {step.department_name ? ` · ${step.department_name}` : ''}
                  </div>
                )}
                {!!step.note && (
                  <div style={{ fontSize: 12, color: '#334155', marginTop: 8, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {step.note}
                  </div>
                )}
                {(step.files || []).length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {step.files.map((f, fi) => (
                      <a
                        key={fi}
                        href={BASE_MEDIA_URL + (f.file_name || f)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 11, color: '#233A7A', textDecoration: 'none' }}
                      >
                        <Paperclip size={12} weight="regular" /> {f.original_name || f.file_name || f}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
